import { UseAxiosResult } from 'axios-hooks'
import { useAxiosClient } from './configs/axios-setup'
import { AccessTokenKey, GMMSRaceTeamKey, RescaleTokenKey } from '../utils/constants'

export interface IRequestError {
  status: number
  text: string
}

export const useGetRHRepoSets = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/RHRepoSets',
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = query => {
    return doFetch({
      url: '/RHRepoSets' + (query ? `?${query}` : '')
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useGetRHRepoSet = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = id => {
    return doFetch({
      url: `/RHRepoSets/${id}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useCreateRHRepoSet = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/RHRepoSets',
      method: 'POST'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = payload => {
    return doFetch({
      data: payload.data,
      params: payload.params
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useUpdateRHRepoSet = (): UseAxiosResult => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      method: 'PUT'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = payload => {
    return doFetch({
      url: `/RHRepoSets/${payload.id}`,
      data: payload.data,
      ...(payload.params ? { params: payload.params } : {})
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export type FinalizeRHProgress = {
  percent: number
  completed: number
  total: number
}

/**
 * Finalize ride height files for a saved RHRepoSet, streaming live progress.
 *
 * The backend decompresses the staged archive and uploads each file, emitting
 * newline-delimited JSON progress events. This function reads that stream and
 * invokes `onProgress` with a 0-100 percentage. It resolves when finalization
 * completes and rejects if the backend reports a failure.
 */
export const finalizeRHRepoSetStreaming = async (
  params: { id: string; rhAeroTempRunId: string; archiveAliases?: Record<string, string> },
  onProgress?: (progress: FinalizeRHProgress) => void
): Promise<void> => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || ''
  const token = localStorage.getItem(AccessTokenKey)
  const teamId = localStorage.getItem(GMMSRaceTeamKey) || ''
  const rescaleToken = localStorage.getItem(RescaleTokenKey) || ''

  // Mirror the axios apiClient headers. x-api-key is only sent when configured
  // (axios omits undefined headers); the CORS allow-list does not include it,
  // so sending an empty value would fail the preflight.
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    AuthorizationRescale: rescaleToken,
    GMMSRaceTeamId: teamId
  }
  if (process.env.API_KEY) {
    headers['x-api-key'] = process.env.API_KEY
  }

  const response = await fetch(`${baseUrl}/RHRepoSets/${params.id}/finalize-rh-files`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      rhAeroTempRunId: params.rhAeroTempRunId,
      ...(params.archiveAliases && Object.keys(params.archiveAliases).length > 0
        ? { archiveAliases: params.archiveAliases }
        : {})
    })
  })

  if (!response.ok || !response.body) {
    throw new Error(`Failed to finalize ride height files (status ${response.status})`)
  }

  // Read the response body as a stream, decode it, and process each line as JSON.
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let finalError: string | null = null
  let completed = false

  const handleLine = (line: string) => {
    const trimmed = line.trim()
    if (!trimmed) return
    try {
      const evt = JSON.parse(trimmed)
      if (evt.type === 'progress') {
        onProgress?.({
          percent: typeof evt.percent === 'number' ? evt.percent : 0,
          completed: evt.completed ?? 0,
          total: evt.total ?? 0
        })
      } else if (evt.type === 'done') {
        completed = true
        onProgress?.({ percent: 100, completed: evt.movedFiles ?? 0, total: evt.movedFiles ?? 0 })
      } else if (evt.type === 'error') {
        finalError = evt.message || 'Decompression failed'
      }
    } catch {
      // ignore malformed lines
    }
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Read the next chunk from the stream.
    const { done, value } = await reader.read()
    if (done) break
    // Decode the chunk and append it to the buffer, then process any complete lines.
    buffer += decoder.decode(value, { stream: true })
    let newlineIndex = buffer.indexOf('\n')
    // While there are complete lines in the buffer, handle them and remove them from the buffer.
    while (newlineIndex >= 0) {
      handleLine(buffer.slice(0, newlineIndex))
      buffer = buffer.slice(newlineIndex + 1)
      newlineIndex = buffer.indexOf('\n')
    }
  }
  // Flush any trailing buffered content
  handleLine(buffer)

  if (finalError) throw new Error(finalError)
  if (!completed) throw new Error('Decompression did not complete')
}
